<template>
  <div class="dashboard-container">
    <!-- Header with Date Range Selector -->
    <div class="dashboard-header" :class="{ 'mobile': isMobile }">
      <div class="header-left">
        <h1 class="page-title">
          {{ $t('dashboard.home.title') }}
          <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltip')">
            <i class="bi bi-question-circle ms-2 help-icon"></i>
          </span>
        </h1>
      </div>
      <div class="header-right">
        <DateRangePicker v-model="dateRange" :min-date="minDate" :max-date="maxDate" />
        <a href="#" class="reset-link" @click.prevent="resetDateRange">
          <i class="bi bi-arrow-counterclockwise me-1"></i>{{ $t('common.resetView') }}
        </a>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="dashboard-content">
      <!-- Row 1: Visitors + Visitors per area -->
      <div class="row-1">
        <VisitorCard :dateRange="dateRange" />
        <DailyVisitorCard :dateRange="dateRange" />
      </div>

      <!-- Row 2: Repeat visitors + Higashikawa Stock Holders -->
      <div class="row-2">
        <RepeatVisitorCard :dateRange="dateRange" />
        <HigashikawaStockholderVisitorCard :dateRange="dateRange" />
      </div>

      <!-- Row 3: Ages + Genders -->
      <div class="row-3">
        <AgeGroupChartCard :dateRange="dateRange" />
        <GenderChartCard :dateRange="dateRange" />
      </div>

      <!-- Row 4: Accommodation Occupancy + Events Statuses | Event Bookings + Top Events -->
      <div class="row-4">
        <!-- Left Column: Accommodation Occupancy + Events Statuses -->
        <div class="left-column">
          <OccupancyCard :dateRange="dateRange" />
          <CrowdLevelCalendarCard :dateRange="dateRange" />
        </div>

        <!-- Right Column: Event Bookings + Top Events -->
        <div class="right-column">
          <EventsCombinedCard :dateRange="dateRange" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Popover } from 'bootstrap';
import VisitorCard from '../../components/user/dashboard/VisitorCard.vue';
import DailyVisitorCard from '../../components/user/dashboard/DailyVisitorCard.vue';
import RepeatVisitorCard from '../../components/user/dashboard/RepeatVisitorCard.vue';
import HigashikawaStockholderVisitorCard from '../../components/user/dashboard/HigashikawaStockholderVisitorCard.vue';
import AgeGroupChartCard from '../../components/user/dashboard/AgeGroupChartCard.vue';
import GenderChartCard from '../../components/user/dashboard/GenderChartCard.vue';
import OccupancyCard from '../../components/user/dashboard/OccupancyCard.vue';
import CrowdLevelCalendarCard from '../../components/user/dashboard/CrowdLevelCalendarCard.vue';
import EventsCombinedCard from '../../components/user/dashboard/EventsCombinedCard.vue';
import DateRangePicker from '../../components/user/dashboard/DateRangePicker.vue';
import { useSharedDateRange, defaultDateRange } from '../../composables/useSharedDateRange';
import { fetchDateRangeLimits } from '../../utils/statisticDataUtils.js';
import { useIsMobile } from '../../utils/DeviceUtil.js';
import { colors } from '../../../consts/colors.js';

// Mobile detection
const { isMobile } = useIsMobile();

// Date range state - shared across dashboard, events and accommodations
const { dateRange } = useSharedDateRange();

/**
 * 期間をデフォルト(今日〜7日後)に戻す
 */
const resetDateRange = () => {
  dateRange.value = defaultDateRange();
};

// Date range limits
const minDate = ref(null);
const maxDate = ref(null);

// Load date range limits
const loadDateRangeLimits = async () => {
  const { minDate: min, maxDate: max } = await fetchDateRangeLimits();
  minDate.value = min;
  maxDate.value = max;
};

// Initialize Bootstrap popovers and fetch date range limits
onMounted(() => {
  // Set CSS variables for colors
  const root = document.documentElement;
  root.style.setProperty('--color-background', colors.background);
  root.style.setProperty('--color-text-primary', colors.textPrimary);
  root.style.setProperty('--color-text-light', colors.textLight);
  root.style.setProperty('--color-white', colors.white);

  const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
  [...popoverTriggerList].map(popoverTriggerEl => new Popover(popoverTriggerEl, {
    customClass: 'custom-popover'
  }));
  loadDateRangeLimits();
});
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 24px;
  background-color: var(--color-background);
  min-height: 100vh;
  min-width: 370px;
  overflow-x: auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  &.mobile {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;

    .header-left {
      width: 100%;
    }

    .header-right {
      width: 100%;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .reset-link {
      font-size: 13px;
      color: v-bind('colors.btnBgColorPrimary');
      text-decoration: none;
      white-space: nowrap;
    }
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
    display: flex;
    align-items: center;

    .help-icon {
      font-size: 18px;
      color: var(--color-text-light);
      cursor: pointer;
    }
  }
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// Row 1: Visitors + Visitors per area
.row-1 {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
}

// Row 2: Frequent Visitor + Higashikawa Stock Holders
.row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

// Row 3: Ages + Genders
.row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

// Row 4: Accommodation Occupancy + Events Statuses | Event Bookings + Top Events
.row-4 {
  display: grid;
  grid-template-columns: 3fr 6fr;
  gap: 16px;
  align-items: stretch;

  .left-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    max-width: 100%;

    // Remove margin-bottom from cards since gap handles spacing
    :deep(.card) {
      margin-bottom: 0;
    }

    // Make the last card (CrowdLevelCalendarCard) stretch to fill remaining space
    > :last-child {
      flex: 1;
    }
  }

  .right-column {
    display: flex;
    flex: 1;
    min-width: 0;
    width: 100%;

    // Remove margin-bottom from the card
    :deep(.card) {
      margin-bottom: 0;
    }
  }
}

@media (max-width: 1200px) {
  .dashboard-content {
    .row-1, .row-2, .row-3, .row-4 {
      grid-template-columns: 1fr;
    }
  }
}
</style>

<style lang="scss">
// Global styles for custom popover (not scoped)
.custom-popover {
  .popover-body {
    background-color: var(--color-text-primary);
    color: var(--color-white);
  }

  &.bs-popover-auto[data-popper-placement^="top"] > .popover-arrow::after,
  &.bs-popover-top > .popover-arrow::after {
    border-top-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="bottom"] > .popover-arrow::after,
  &.bs-popover-bottom > .popover-arrow::after {
    border-bottom-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="left"] > .popover-arrow::after,
  &.bs-popover-start > .popover-arrow::after {
    border-left-color: var(--color-text-primary);
  }

  &.bs-popover-auto[data-popper-placement^="right"] > .popover-arrow::after,
  &.bs-popover-end > .popover-arrow::after {
    border-right-color: var(--color-text-primary);
  }
}
</style>
